import type {Reseller} from '../server/functions/resellers/resellers.types.js'
import type {Route, NewRouteTemplate} from '../server/functions/route/route.types.js'

type MessengerSettingsSource = 'route' | 'reseller' | 'disabled'

type AdjustableByMessengerGrouped = Route['adjustableByMessengerGrouped']
type ResellerSettings = Partial<Reseller['settings']>

type RouteMessengerSettings = Pick<Route,
    | 'adjustableByMessengerGrouped'
    | 'adjustableByMessenger'
    | 'allowAddAddress'
    | 'allowAddCollo'
    | 'messengerAddOrderWarning'
    | 'showWarningOnAddExistingOrder'
    | 'allowMessengerEditCustomOptions'
>

type TemplateMessengerSettings = Pick<NewRouteTemplate['routeCalculation'],
    | 'adjustableByMessengerGrouped'
    | 'adjustableByMessenger'
    | 'allowAddAddress'
    | 'allowAddCollo'
    | 'messengerAddOrderWarning'
    | 'showWarningOnAddExistingOrder'
    | 'allowMessengerEditCustomOptions'
>

type MessengerSettingsInput = Partial<RouteMessengerSettings | TemplateMessengerSettings>

type ResellerMessengerSettings = Pick<Route,
    | 'adjustableByMessenger'
    | 'allowAddAddress'
    | 'allowAddCollo'
    | 'messengerAddOrderWarning'
    | 'showWarningOnAddExistingOrder'
    | 'allowMessengerEditCustomOptions'
>

type ResolvedMessengerSettings = RouteMessengerSettings & {
    source: MessengerSettingsSource
    groupedEnabled: boolean
}

const validMessengerAddOrderWarningOptions = ['allowed', 'only_today', 'only_unplanned', 'only_unplanned_today', 'notallowed'] as const

type NormalizedMessengerAddOrderWarning = typeof validMessengerAddOrderWarningOptions[number]

function normalizeAdjustableByMessengerGrouped(value: unknown): AdjustableByMessengerGrouped {
    if (value === true) {
        return 'yes'
    }

    if (value === false) {
        return 'no'
    }

    if (value === 'default' || value === 'yes' || value === 'no') {
        return value
    }

    return 'default'
}

function normalizeMessengerAddOrderWarning(value: unknown): NormalizedMessengerAddOrderWarning {
    if (value === 'warning') {
        return 'allowed'
    }

    if (validMessengerAddOrderWarningOptions.includes(value as NormalizedMessengerAddOrderWarning)) {
        return value as NormalizedMessengerAddOrderWarning
    }

    return 'notallowed'
}

function canAddOrderToRouteWithWarningSetting(
    messengerAddOrderWarning: unknown,
    scannedOrder: {date?: string | null, routeId?: string | null} | null | undefined,
    route: {date?: string | null} | null | undefined
): boolean {
    if (!scannedOrder) {
        return false
    }

    const normalizedMessengerAddOrderWarning = normalizeMessengerAddOrderWarning(messengerAddOrderWarning)
    const isOrderForRouteDate = !!scannedOrder.date && !!route?.date && scannedOrder.date === route.date
    const isOrderUnplanned = !scannedOrder.routeId

    switch (normalizedMessengerAddOrderWarning) {
        case 'allowed':
            return true
        case 'only_today':
            return isOrderForRouteDate
        case 'only_unplanned':
            return isOrderUnplanned
        case 'only_unplanned_today':
            return isOrderUnplanned && isOrderForRouteDate
        default:
            return false
    }
}

function getResellerMessengerSettings(resellerSettings: ResellerSettings = {}): ResellerMessengerSettings {
    return {
        adjustableByMessenger: !!resellerSettings.adjustableByMessenger,
        allowAddAddress: !!resellerSettings.allowAddAddress,
        allowAddCollo: resellerSettings.allowAddCollo || '',
        messengerAddOrderWarning: normalizeMessengerAddOrderWarning(resellerSettings.messengerAddOrderWarning),
        showWarningOnAddExistingOrder: !!resellerSettings.showWarningOnAddExistingOrder,
        allowMessengerEditCustomOptions: !!resellerSettings.orders?.allowMessengerEditCustomOptions
    }
}

function getMessengerSettingsSource(adjustableByMessengerGrouped: unknown, resellerSettings: ResellerSettings = {}): MessengerSettingsSource {
    const mode = normalizeAdjustableByMessengerGrouped(adjustableByMessengerGrouped)

    if (mode === 'yes') {
        return 'route'
    }

    if (mode === 'default' && !!resellerSettings.adjustableByMessengerGrouped) {
        return 'reseller'
    }

    return 'disabled'
}

function resolveMessengerRouteSettings(settings: MessengerSettingsInput | null | undefined = {}, resellerSettings: ResellerSettings = {}): ResolvedMessengerSettings {
    const safeSettings = settings || {}
    const adjustableByMessengerGrouped = normalizeAdjustableByMessengerGrouped(safeSettings.adjustableByMessengerGrouped)
    const source = getMessengerSettingsSource(adjustableByMessengerGrouped, resellerSettings)

    if (source === 'route') {
        return {
            adjustableByMessengerGrouped,
            source,
            groupedEnabled: true,
            adjustableByMessenger: !!safeSettings.adjustableByMessenger,
            allowAddAddress: !!safeSettings.allowAddAddress,
            allowAddCollo: safeSettings.allowAddCollo || '',
            messengerAddOrderWarning: normalizeMessengerAddOrderWarning(safeSettings.messengerAddOrderWarning),
            showWarningOnAddExistingOrder: !!safeSettings.showWarningOnAddExistingOrder,
            allowMessengerEditCustomOptions: !!safeSettings.allowMessengerEditCustomOptions
        }
    }

    if (source === 'reseller') {
        return {
            adjustableByMessengerGrouped,
            source,
            groupedEnabled: true,
            ...getResellerMessengerSettings(resellerSettings)
        }
    }

    return {
        adjustableByMessengerGrouped,
        source,
        groupedEnabled: false,
        adjustableByMessenger: false,
        allowAddAddress: false,
        allowAddCollo: '',
        messengerAddOrderWarning: 'notallowed',
        showWarningOnAddExistingOrder: false,
        allowMessengerEditCustomOptions: false
    }
}

export {
    normalizeAdjustableByMessengerGrouped,
    getResellerMessengerSettings,
    getMessengerSettingsSource,
    resolveMessengerRouteSettings,
    canAddOrderToRouteWithWarningSetting
}

export type {
    AdjustableByMessengerGrouped,
    MessengerSettingsSource,
    MessengerSettingsInput,
    ResellerMessengerSettings,
    ResolvedMessengerSettings
}
